import React, {useEffect, useState} from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useGetAllDealsQuery, useGetAllContactsQuery, useGetAllActivitiesQuery } from "state/api";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import SceneBox from "components/SceneBox";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import { FlagOutlined, Liquor, Euro, QueryStats, BusinessOutlined } from "@mui/icons-material";
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import getAccessToken from "state/accessToken";

function formatCurrency(value) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
}

function getWeeklyEarnings(deals) {
  const startOfWeek = moment().startOf('isoWeek');
  const endOfWeek = moment().endOf('isoWeek');

  let weekEarnings = 0;

  deals.forEach(deal => {
    if (deal.status === 'Won' && deal.closedDate != null) {
      const closedDate = moment.unix(deal.closedDate);
      const dealValue = parseFloat(deal.dealValue);

      if (closedDate.isBetween(startOfWeek, endOfWeek, null, '[]') && !isNaN(dealValue)) {
        weekEarnings += dealValue;
      }
    }
  });

  return formatCurrency(weekEarnings);
}

function getYearlyEarnings(deals) {
  const startOfYear = moment().startOf('year');
  const endOfYear = moment().endOf('year');

  const yearlyEarnings = deals
    .filter(deal => deal.status === 'Won' && deal.closedDate)
    .reduce((total, deal) => {
      const closedDate = moment.unix(deal.closedDate);
      if (closedDate.isBetween(startOfYear, endOfYear, null, '[]')) {
        return total + parseFloat(deal.dealValue || 0);
      }
      return total;
    }, 0);

  return formatCurrency(yearlyEarnings);
}

const Dashboard = () => {
  
  const theme = useTheme();
  const isNonMediumScreens = useMediaQuery("(min-width: 1200px)");
  const { data: dealsData, isLoading } = useGetAllDealsQuery();
  const deals = dealsData?.deals || [];
  const total = dealsData?.total || 0;
  const { data: contactsData } = useGetAllContactsQuery();
  const totalClients = contactsData?.total || 0;
  const { data: activitiesData, loadingAct, error } = useGetAllActivitiesQuery();
  const activities = activitiesData?.activities || [];
  const totalActivities = activitiesData?.total || 0;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const weeklyEarnings = getWeeklyEarnings(deals);
  const yearlyEarnings = getYearlyEarnings(deals);
  const [nextEvents, setNextEvents] = useState([]);
  const navigate = useNavigate();

  const redirectToCalendar = () => {
    navigate('/calendar');
  };

  const fetchNextEvents = async () => {
      try {
          const accessToken = await getAccessToken();
          if (!accessToken) {
              console.error('No access token available');
              return [];
          }

          const today = new Date();
          const endRange = new Date();
          endRange.setDate(today.getDate() + 7); // Fetch events for the next 7 days

          const response = await fetch(`https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin=${today.toISOString()}&timeMax=${endRange.toISOString()}&orderBy=startTime&singleEvents=true`, {
              headers: {
                  'Authorization': `Bearer ${accessToken}`,
              },
          });
          
          if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          const filteredEvents = data.items
              .filter(event => new Date(event.start.dateTime || event.start.date) >= today)
              .sort((a, b) => new Date(a.start.dateTime || a.start.date) - new Date(b.start.dateTime || b.start.date))
              .slice(0, 8); // Get only the next 8 events

          return filteredEvents;
      } catch (error) {
          console.error('Error fetching events', error);
          return [];
      }
  };

  useEffect(() => {
    fetchNextEvents().then(events => {
      setNextEvents(events);
    });
  }, []);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };

  const columns = [
        {
        field: "id",
        headerName: "ID",
        minWidth: 30,
        flex: isMobile ? 0 : 0.3,
        },
        {
        field: "type",
        headerName: "Type",
        minWidth: 30,
        flex: isMobile ? 0 : 0.3,
        },
        {
        field: "title",
        headerName: "Title",
        minWidth: 120,
        flex: isMobile ? 1 : 1.2,
        },
        {
        field: "dueDate",
        headerName: "Date",
        minWidth: 20,
        flex: isMobile ? 0 : 0,
        valueFormatter: (params) => formatDate(params.value),
        },
        {
        field: "completedAt",
        headerName: "Completed",
        minWidth: 20,
        flex: isMobile ? 0 : 0.2,
        valueFormatter: (params) => formatDate(params.value),
        },
        {
        field: "description",
        headerName: "Description",
        minWidth: 200,
        flex: isMobile ? 0 : 1,
        },
        {
        field: "tags",
        headerName: "Tags",
        minWidth: 50,
        flex: isMobile ? 0 : 0.5,
        },
    ];

  return (
    <Box m="1.5rem 2.5rem">
      <FlexBetween>
        <Header title="DASHBOARD" subtitle="Welcome to your dashboard" />
        <Box>
        </Box>
      </FlexBetween>

      <Box
        mt="20px"
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        gridAutoRows="160px"
        gap="20px"
        sx={{
          "& > div": { gridColumn: isNonMediumScreens ? undefined : "span 12" },
        }}
      >
        {/* ROW 1 */}
        <Box
            gridColumn="span 4"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            p="0.5rem 1rem"
            flex="1 1 100%"
            backgroundColor={theme.palette.background.alt}
            borderRadius="0.55rem"
            sx={{ cursor: "pointer" }}
        >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginBottom={1}
            >
              <Typography variant="h4" color="#808080">Upcoming Events</Typography>
              <Typography 
                variant="body1" 
                color={theme.palette.secondary.main} 
                sx={{ cursor: 'pointer' }}
                onClick={redirectToCalendar}
              >
                See Calendar
              </Typography>
            </Box>
            <Box display="flex" width="100%">
                {/* Column 1 */}
                <Box flex="1" marginRight="0.5rem" p="0.2rem 1rem">
                    {nextEvents.slice(0, 4).map((event, index) => (
                        <Box 
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: 1,
                            '&:hover': {
                              backgroundColor: '#f55151',
                              cursor: 'pointer',
                            },
                          }}
                          onClick={() => window.open(event.htmlLink, '_blank')}
                        >
                            <Typography variant="body1" sx={{ marginRight: 1 }}>
                                <strong>{moment(event.start.dateTime || event.start.date).format('DD/MM')}</strong>
                            </Typography>
                            <Typography color="#c2c2c2" variant="body2" sx={{ fontSize: '0.8em', marginRight: 1 }}>
                                {moment(event.start.dateTime || event.start.date).format('LT')}
                            </Typography>
                            <Typography variant="body2" sx={{fontSize: '1em'}}>
                                <strong>{event.summary}</strong>
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Vertical Divider */}
                <Box
                    width="2px"
                    backgroundColor="#e24646"
                    marginX="2rem"
                    alignSelf="stretch"
                    height={110}
                />

                {/* Column 2 */}
                <Box flex="1" marginRight="0.5rem" p="0.2rem 1rem">
                    {nextEvents.slice(4, 8).map((event, index) => (
                        <Box 
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: 1,
                            '&:hover': {
                              backgroundColor: '#f55151',
                              cursor: 'pointer',
                            },
                          }}
                          onClick={() => window.open(event.htmlLink, '_blank')}
                        >
                            <Typography variant="body1" sx={{ marginRight: 1 }}>
                                <strong>{moment(event.start.dateTime || event.start.date).format('DD/MM')}</strong>
                            </Typography>
                            <Typography color="#c2c2c2" variant="body2" sx={{ fontSize: '0.8em', marginRight: 1 }}>
                                {moment(event.start.dateTime || event.start.date).format('LT')}
                            </Typography>
                            <Typography variant="body2" sx={{fontSize: '1em'}}>
                                <strong>{event.summary}</strong>
                            </Typography>
                        </Box>
                    ))}
              </Box>
          </Box>
        </Box>
        <SceneBox
          value="Discord Office"
          onClick={() => {
                window.open('https://discord.com/channels/1075164060204671109/1075164062318604361');
              }}
          icon={
            <BusinessOutlined
              sx={{ color: theme.palette.secondary[500], fontSize: "36px" }}
            />
          }
        />

        {/* ROW 2 */}
        <Box
          gridColumn="span 12"
            gridRow="span 3"
            sx={{
                "& .MuiDataGrid-root": {
                border: "none",
                borderRadius: "5rem",
                },
                "& .MuiDataGrid-cell": {
                borderBottom: "none",
                width: '100%',
                },
                "& .MuiDataGrid-columnHeaders": {
                backgroundColor: theme.palette.background.alt,
                color: theme.palette.secondary[300],
                borderBottom: "none",
                },
                "& .MuiDataGrid-virtualScroller": {
                backgroundColor: theme.palette.background.alt,
                },
                "& .MuiDataGrid-footerContainer": {
                backgroundColor: theme.palette.background.alt,
                color: theme.palette.secondary[100],
                borderTop: "none",
                },
                "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                color: `${theme.palette.secondary[200]} !important`,
                },
          }}
        >
          <Box
            sx={{ color: theme.palette.secondary[300], fontSize: "20px" }}>
            Activities
          </Box>
          <DataGrid
            loading={loadingAct || !activitiesData}
            getRowId={(activity) => activity.id}
            rows={activities}
            columns={columns}
            rowCount={totalActivities}
            components={{
                    Toolbar: DataGridCustomToolbar

                }}
          />
          
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;